import { Box, Typography, Button } from '@mui/material';
import { Check } from '@mui/icons-material';
import { computePricing, getPlanBullets } from './planCardUtils';

const cardStyles = {
  colBorder: {
    border: '1px solid #C7D0DE',
    borderRadius: '16px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 8px 20px rgba(21, 108, 254, 0.08)',
    width: '290px',
    minHeight: { xxs: '240px', sm: '260px' },
    padding: { xxs: '32px 18px 20px', sm: '36px 20px 24px' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: { xxs: '12px', sm: '16px' },
    position: 'relative',
  },
  colHeader: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pb: { xxs: '4px', sm: '6px' },
    flexGrow: 1,
    position: 'relative',
    minHeight: '130px',
  },
  textHeadline: {
    fontWeight: 800,
    fontSize: { xxs: '20px', sm: '26px' },
    color: '#232B35',
  },
  blackPrice: {
    color: '#232B35',
    fontWeight: 800,
    fontSize: { xxs: '28px', sm: '30px' },
  },
  priceLabel: {
    fontWeight: 400,
    fontSize: '10px',
    color: '#232B35',
    justifyContent: 'center',
    textAlign: 'center',
  },
  priceNote: {
    fontWeight: 700,
    fontSize: '10px',
    color: '#3171F6',
    justifyContent: 'center',
    textAlign: 'center',
    mb: '8px',
    mt: '-4px',
  },
  bulletRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    width: '100%',
    color: '#232B35',
    fontSize: '13px',
    fontWeight: 400,
    textAlign: 'left',
    letterSpacing: '-0.43px',
  },
  checkIcon: {
    color: '#000000',
    fontSize: { xxs: '18px', sm: '20px' },
    mt: '2px',
  },
  upgradeButton: {
    borderRadius: '30px',
    width: '200px',
    height: '40px',
    fontWeight: 700,
    fontSize: '14px',
    padding: 0,
    background: '#3171F6',
    textTransform: 'none',
    color: '#FFFFFF',
    '&:hover': {
      background: '#3171F6',
    },
  },
};

const tagByOption = {
  2: 'Most popular!',
};

export default function CardPlanCode({
  plan,
  options,
  isAnnual,
  fullWidth = false,
  hideButton = false,
}) {
  if (!plan || !options) return null;

  const price = computePricing(
    options.monthly,
    options.annual,
    Boolean(isAnnual)
  );
  const boardCount =
    plan?.boardsCanJoinCount ?? plan?.salesforceValues?.boardsCanJoinCount;
  const bullets = getPlanBullets(options.option, boardCount);
  const tag = tagByOption[options.option];
  const rootStyles = {
    ...cardStyles.colBorder,
    width: fullWidth ? '100%' : cardStyles.colBorder.width,
    gap: hideButton ? '0px' : cardStyles.colBorder.gap,
  };

  return (
    <Box sx={rootStyles}>
      {tag ? (
        <Typography
          sx={{
            display: 'inline-block',
            backgroundColor: '#3171F6',
            color: '#FFFFFF',
            px: '18px',
            py: '2px',
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: { xxs: '10px', sm: '12px' },
            position: 'absolute',
            top: '8px',
            right: '10px',
          }}
        >
          {tag}
        </Typography>
      ) : (
        <Box
          sx={{
            position: 'absolute',
            top: { xxs: '12px', sm: '16px' },
            height: { xxs: '22px', sm: '24px' },
          }}
        />
      )}

      <Box sx={{...cardStyles.colHeader, minHeight:'100px'}}>
        <Typography sx={cardStyles.textHeadline}>
          {plan?.salesforceValues?.tier || '—'}
        </Typography>

        <Typography sx={cardStyles.blackPrice}>
          {price.line1Main}{' '}
          <Typography component="span" sx={cardStyles.priceLabel}>
            {price.line1Suffix}
          </Typography>
        </Typography>

        <Typography sx={cardStyles.priceNote}>{price.line2}</Typography>
        {!hideButton && (
          <Button
            variant="contained"
            size="small"
            sx={{ ...cardStyles.upgradeButton, pointerEvents: 'none' }}
            disableRipple
            tabIndex={-1}
          >
            Upgrade
          </Button>
        )}
      </Box>

      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          mt: !hideButton ? { xxs: '8px', sm: '12px' } : 0,
        }}
      >
        {bullets.map((content, index) => (
          <Typography component="li" key={index} sx={cardStyles.bulletRow}>
            <Check sx={cardStyles.checkIcon} />
            <span>{content}</span>
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
